import { ComponentPropsWithoutRef, ReactElement, VFC } from 'react';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';
import useSWR from 'swr';

import * as Sentry from '@sentry/nextjs';
import { API, Structure } from '@/types/app';

import Card from '@/components/list/Card';

/**
 * @component
 */
const CardRow: VFC<
  {
    type?: keyof typeof Structure.PagedResponseAssetInfoType;
  } & ComponentPropsWithoutRef<'div'>
> = ({ className, type, ...props }): ReactElement => {
  const { t } = useTranslation(['detail', 'common']);

  const { data: popularData, error: popularError } = useSWR<API.Response<Structure.AssetInfo[]>>(
    `/api/data/getPopular?count=1${type ? `&type=${type}` : ''}`,
  );

  const { data: topGainsData, error: topGainsError } = useSWR<
    API.Response<Structure.PagedResponseAssetInfo>
  >(
    `/api/data/findAssets?page=0&pageSize=1&sortBy=changePercent24H&sortOrder=DESC${
      type ? `&type=${type}` : ''
    }`,
  );

  const { data: topLossesData, error: topLossesError } = useSWR<
    API.Response<Structure.PagedResponseAssetInfo>
  >(
    `/api/data/findAssets?page=0&pageSize=1&sortBy=changePercent24H&sortOrder=ASC${
      type ? `&type=${type}` : ''
    }`,
  );

  if (popularError)
    Sentry.captureException(new Error(popularError?.message), {
      extra: {
        popularData,
        popularError,
      },
    });

  if (topGainsError)
    Sentry.captureException(new Error(topGainsError?.message), {
      extra: {
        topGainsData,
        topGainsError,
      },
    });

  if (topLossesError)
    Sentry.captureException(new Error(topLossesError?.message), {
      extra: {
        topLossesData,
        topLossesError,
      },
    });

  return (
    <div
      className={cn('grid grid-cols-1 gap-3 tablet:grid-cols-2 desktop:grid-cols-3', className)}
      {...props}
    >
      <Card category={t('categories.0')} instrument={topGainsData?.result?.data?.[0]} />
      <Card category={t('categories.1')} instrument={topLossesData?.result?.data?.[0]} />
      <Card
        category={t('categories.3')}
        instrument={popularData?.result?.[0]}
        className="hidden desktop:block"
      />
    </div>
  );
};

export default CardRow;
